import { KnownId } from "@fairfleet/geotab";
import { DeviceWithVehicleInfo } from "@/models/DeviceWithVehicleInfo";
import { LiveDataRow } from "@/pages/live-data/models/LiveDataRow";
import { canProtocolNames } from "@/pages/live-data/utilities/checklistGroups";
import { odometerValue } from "@/pages/live-data/utilities/checklistValues";
import {
  CheckKey,
  isFuelLevelPercentRow,
  isFuelLevelVolumeRow,
  isOdometerRow,
  isRawOdometerRow,
  matchesCheck,
} from "@/pages/live-data/utilities/diagnosticMatchers";
import { ioxDeviceNames } from "@/pages/live-data/utilities/ioxDevices";
import { newestOf } from "@/pages/live-data/utilities/newestReading";
import { getFirmwareValue } from "@/utilities/getFirmwareValue";

/**
 * How a vehicle came out of the check overall.
 *
 * "Not scanned" is a state of its own. Without it a vehicle nobody has looked at yet is
 * indistinguishable from one that was looked at and reported nothing — so opening the page showed
 * the entire fleet as failed, and a search that turned up a vehicle outside the scan showed it as
 * failed too.
 */
export type CheckStatus = "notScanned" | "ok" | "warn" | "alert";

/**
 * The values shown as one tick per column.
 *
 * A list rather than separate fields, so the columns can be generated from it — the original had
 * eleven near-identical column definitions and eleven near-identical lookups beside them.
 */
export const REPORTED_CHECKS = [
  "ignition",
  "vehicleSpeed",
  "engineSpeed",
  "deviceVoltage",
  "fuelLevel",
  "fuelLevelLiters",
  "defLevel",
  "hvBatteryVoltage",
  "hvBatteryCurrent",
  "hvBatteryPower",
  "stateOfCharge",
] as const;

export type ReportedCheck = (typeof REPORTED_CHECKS)[number];

/**
 * The two checks that are not simply a rule of their own name.
 *
 * Both read the same diagnostic and are told apart by its unit, so they cannot be addressed by a
 * key. Listing only the exceptions keeps that fact visible instead of burying it in a table where
 * every other entry repeats its own key.
 */
const UNIT_SPLIT: Partial<Record<ReportedCheck, (row: LiveDataRow) => boolean>> = {
  fuelLevel: isFuelLevelPercentRow,
  fuelLevelLiters: isFuelLevelVolumeRow,
};

function predicateFor(check: ReportedCheck): (row: LiveDataRow) => boolean {
  return UNIT_SPLIT[check] ?? (row => matchesCheck(check as CheckKey, row));
}

/**
 * How far the two odometers may differ before it is worth pointing out, in whatever unit they
 * are shown in. Below that they disagree only by rounding.
 */
const ODOMETER_TOLERANCE = 1;

/**
 * Geotab's GPS odometer, as MyGeotab shows it with "(verwendet jetzt GPS)": the odometer
 * adjustment as Geotab interpolates it, so without an id. Real adjustment rows are entered by hand.
 */
function isGpsOdometerRow(row: LiveDataRow): boolean {
  return row.diagnosticId === KnownId.DiagnosticOdometerAdjustmentId && !row.id;
}

/** One vehicle's line in the fleet-wide check. */
export interface FleetCheckRow {
  id: string;
  device: DeviceWithVehicleInfo;
  /** The readings the line was built from, for the expandable checklist underneath it. */
  rows: LiveDataRow[];
  name: string;
  licensePlate: string;
  serial: string;
  vin: string;
  /** Make and model as decoded from the VIN, where that lookup answered. */
  vinDecoded: string;
  /** Whether the telematics unit is currently communicating; `undefined` while not yet known. */
  isCommunicating: boolean | undefined;
  /** The newest reading of any kind, which says how current everything else on the line is. */
  lastRecordAt: Date | null;
  rawOdometer: number | null;
  /** Each carries its own unit: the measurement profile can cover one of the two and not the other. */
  rawOdometerUnit: string;
  odometer: number | null;
  odometerUnit: string;
  /** Geotab's GPS-kept odometer at the end of the window; `null` where not asked for. */
  gpsOdometer: number | null;
  gpsOdometerUnit: string;
  /** The two odometers disagree by more than rounding, in the same unit. */
  odometerMismatch: boolean;
  reported: Record<ReportedCheck, boolean>;
  firmware: string;
  /** The value of the harness reading as the unit sent it, or empty where none was reported. */
  harness: string;
  fmsStandardSoftwareVersion: string;
  canProtocols: string[];
  ioxDevices: string[];
  status: CheckStatus;
}

export interface BuildFleetCheckRowInput {
  device: DeviceWithVehicleInfo;
  /**
   * The newest reading per diagnostic the vehicle produced inside the scanned window, or `null`
   * where no scan has covered it.
   *
   * One input rather than a list plus a "was it scanned" flag: with two, "not scanned but here
   * are its readings" is a state the type allows and nothing means.
   */
  rows: LiveDataRow[] | null;
  /** Whether the unit is communicating, from the fleet status query. */
  isCommunicating?: boolean;
  /** Make and model decoded from the VIN, where known. */
  vinDecoded?: string;
}

/**
 * Turns one vehicle's readings into its line of the fleet-wide check.
 *
 * The same questions the single-vehicle checklist asks, answered from the same rules — but as one
 * row of ticks rather than a page, and without the 24-hour fallback: the scan already covers a
 * window the caller chose, so "reported inside the window" is the whole question.
 */
export function buildFleetCheckRow(input: BuildFleetCheckRowInput): FleetCheckRow {
  const { device, rows, isCommunicating, vinDecoded = "" } = input;
  const readings = rows ?? [];

  const reported = {} as Record<ReportedCheck, boolean>;
  for (const check of REPORTED_CHECKS) {
    reported[check] = readings.some(predicateFor(check));
  }

  const rawOdometer = odometerValue(readings.find(isRawOdometerRow));
  const odometer = odometerValue(readings.find(isOdometerRow));
  const gpsOdometer = odometerValue(readings.find(isGpsOdometerRow));
  const canProtocols = canProtocolNames(readings);
  const firmware = getFirmwareValue(device);
  const harness = harnessValue(readings);

  // What has to be there before a vehicle can be signed off at all. Everything else on the line
  // depends on what the vehicle is — a diesel reports no state of charge, and that is not a fault.
  const core = [
    reported.ignition,
    reported.vehicleSpeed,
    firmware !== "",
    harness !== "",
    canProtocols.length > 0,
  ];
  const met = core.filter(Boolean).length;

  return {
    id: device.id,
    device,
    rows: readings,
    name: device.name ?? "",
    licensePlate: device.licensePlate ?? "",
    serial: device.serialNumber ?? "",
    vin: device.vehicleIdentificationNumber ?? "",
    vinDecoded,
    isCommunicating,
    lastRecordAt: newestOf(readings, row => row.dateTime)?.dateTime ?? null,
    rawOdometer: rawOdometer?.value ?? null,
    rawOdometerUnit: rawOdometer?.unit ?? "",
    odometer: odometer?.value ?? null,
    odometerUnit: odometer?.unit ?? "",
    gpsOdometer: gpsOdometer?.value ?? null,
    gpsOdometerUnit: gpsOdometer?.unit ?? "",
    // Only comparable in the same unit. The measurement profile can cover one of the two
    // diagnostics and not the other, and then the difference is the unit, not the mileage.
    odometerMismatch:
      rawOdometer !== null &&
      odometer !== null &&
      rawOdometer.unit === odometer.unit &&
      Math.abs(rawOdometer.value - odometer.value) >= ODOMETER_TOLERANCE,
    reported,
    firmware,
    harness,
    fmsStandardSoftwareVersion: reportedValue(readings, "fmsStandardSoftwareVersion"),
    canProtocols,
    ioxDevices: ioxDeviceNames(readings),
    status:
      rows === null
        ? "notScanned"
        : met === core.length
          ? "ok"
          : // Firmware comes from the device record, not from readings, so a silent vehicle still
            // meets one check. No readings at all is what "nothing reported" means.
            met === 0 || readings.length === 0
            ? "alert"
            : "warn",
  };
}

/**
 * Builds the fleet's lines, redoing only the vehicles whose inputs changed.
 *
 * A line costs a regex pass over every reading of the vehicle, and the scan publishes every
 * 500 ms: rebuilding all of a fleet's lines each time is millions of regex runs on the main
 * thread, for the few vehicles a refresh actually touched. An input counts as unchanged when it is
 * the same object, which is what the scan guarantees for a vehicle it did not touch (its readings
 * array keeps its identity); the line then keeps its identity too, so the grid can skip it.
 *
 * Call it with the same function across refreshes. A vehicle that is no longer passed is
 * forgotten.
 */
export function createFleetCheckRowBuilder(): (
  inputs: BuildFleetCheckRowInput[],
) => FleetCheckRow[] {
  let cache = new Map<string, { input: BuildFleetCheckRowInput; row: FleetCheckRow }>();

  return inputs => {
    const next = new Map<string, { input: BuildFleetCheckRowInput; row: FleetCheckRow }>();
    const rows = inputs.map(input => {
      const hit = cache.get(input.device.id);
      const reusable =
        hit !== undefined &&
        hit.input.device === input.device &&
        hit.input.rows === input.rows &&
        hit.input.isCommunicating === input.isCommunicating &&
        hit.input.vinDecoded === input.vinDecoded;

      const entry = reusable ? hit : { input, row: buildFleetCheckRow(input) };
      next.set(input.device.id, entry);
      return entry.row;
    });

    cache = next;
    return rows;
  };
}

/**
 * The value of the harness reading the vehicle sent, which is what the installer compares.
 *
 * The intelligent one wins where both match: it is the more specific answer, and a database that
 * names its reading so that both patterns fit is describing an intelligent harness. Kept as sent,
 * like the FMS version: the number is a code, not a quantity to format.
 */
function harnessValue(rows: LiveDataRow[]): string {
  const row =
    rows.find(candidate => matchesCheck("intelligentHarness", candidate)) ??
    rows.find(candidate => matchesCheck("standardHarness", candidate));
  return row ? String(row.value) : "";
}

/**
 * A value shown as text rather than as a tick.
 *
 * Never run through a number formatter: a software version is an identifier, and "3.1" turned
 * into "3,1" by a German locale is a different string, not a translated one.
 */
function reportedValue(rows: LiveDataRow[], check: "fmsStandardSoftwareVersion"): string {
  const row = rows.find(candidate => matchesCheck(check, candidate));
  return row ? String(row.value) : "";
}
